import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Clock, Phone, Mail, ChevronRight, Check, X, CalendarClock, CheckCircle2, AlertCircle } from "lucide-react";

const STATUS = {
  in_progress: { label: "Offen", color: "#C87A5F", bg: "rgba(200,122,95,0.12)" },
  pending: { label: "Offen", color: "#C87A5F", bg: "rgba(200,122,95,0.12)" },
  confirmed: { label: "Bestätigt", color: "#8B9D83", bg: "rgba(139,157,131,0.16)" },
  alternative_proposed: { label: "Alternative", color: "#B07C2F", bg: "rgba(176,124,47,0.14)" },
  completed: { label: "Abgeschlossen", color: "#6b7280", bg: "rgba(107,114,128,0.12)" },
  cancelled: { label: "Storniert", color: "#b91c1c", bg: "rgba(185,28,28,0.10)" },
};

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.in_progress;
  return (
    <span className="text-[11px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap" style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

export default function TodayView() {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState("");

  const load = () => api.get("/cms/dashboard/today").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (bid, action) => {
    setBusyId(bid + action);
    try {
      await api.post(`/cms/bookings/${bid}/${action}`);
      await load();
    } finally { setBusyId(""); }
  };

  if (!data) return null;
  if (data.bookings.length === 0) {
    return (
      <div className="mv-card p-6 mb-8" data-testid="today-view-empty">
        <div className="flex items-center gap-3">
          <CalendarClock className="w-5 h-5" strokeWidth={1.5} style={{ color: "var(--mv-text-muted)" }} />
          <div>
            <p className="mv-label">Heute</p>
            <p className="text-sm" style={{ color: "var(--mv-text-muted)" }}>Keine Termine heute. Ein ruhiger Tag — perfekt für Theme & Content.</p>
          </div>
        </div>
      </div>
    );
  }

  const nextUp = data.bookings.find((b) => b.id === data.next_up_id);

  return (
    <div className="mv-card p-5 md:p-6 mb-8" data-testid="today-view">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div>
          <p className="mv-label mb-1">Heute · {new Date(data.date).toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" })}</p>
          <h2 className="font-display text-2xl">Tagesplan</h2>
        </div>
        <Link to="/cms/bookings" className="mv-btn-ghost text-xs">
          Alle Buchungen <ChevronRight className="w-3 h-3" />
        </Link>
      </div>

      {nextUp && (
        <div
          className="p-4 rounded-2xl mb-5 flex items-start justify-between gap-3 flex-wrap"
          style={{ background: "var(--mv-primary)", color: "#fff" }}
          data-testid="next-up-card"
        >
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wider opacity-90 mb-1 flex items-center gap-1.5">
              <Clock className="w-3 h-3" /> Nächster Kunde
            </p>
            <p className="font-display text-xl md:text-2xl truncate">{nextUp.customer_name}</p>
            <p className="text-sm opacity-90 mt-0.5">
              {nextUp.time} Uhr · {nextUp.service?.name || nextUp.service_name}
              {nextUp.staff?.name && ` · ${nextUp.staff.name}`}
            </p>
            {nextUp.notes && <p className="text-xs opacity-80 mt-1 italic line-clamp-2">„{nextUp.notes}"</p>}
          </div>
          <div className="flex flex-col items-end gap-2">
            <StatusBadge status={nextUp.status} />
            <div className="flex gap-1.5 flex-wrap justify-end">
              {nextUp.customer_phone && (
                <a href={`tel:${nextUp.customer_phone}`} className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors" aria-label="Anrufen">
                  <Phone className="w-3.5 h-3.5" strokeWidth={1.8} />
                </a>
              )}
              <a href={`mailto:${nextUp.customer_email}`} className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors" aria-label="E-Mail">
                <Mail className="w-3.5 h-3.5" strokeWidth={1.8} />
              </a>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {data.bookings.map((b) => {
          const past = b.time < data.now;
          return (
            <div
              key={b.id}
              data-testid={`today-booking-${b.id}`}
              className="flex items-center gap-3 p-3 rounded-xl border"
              style={{ borderColor: "var(--mv-border)", opacity: b.status === "cancelled" ? 0.55 : (past && b.status !== "completed" ? 0.8 : 1) }}
            >
              <div className="w-14 text-center flex-shrink-0">
                <p className="font-display text-lg leading-none">{b.time}</p>
                <p className="text-[10px] mt-0.5" style={{ color: "var(--mv-text-muted)" }}>
                  {(b.service?.duration_minutes || 0) + (b.addon_duration || 0)} Min
                </p>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium truncate">{b.customer_name}</p>
                  <StatusBadge status={b.status} />
                </div>
                <p className="text-xs truncate" style={{ color: "var(--mv-text-muted)" }}>
                  {b.service?.name || b.service_name}
                  {b.staff?.name && ` · ${b.staff.name}`}
                  {b.total_price > 0 && ` · ${b.total_price.toFixed(2)} €`}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {(b.status === "in_progress" || b.status === "pending" || b.status === "alternative_proposed") && (
                  <button
                    data-testid={`today-confirm-${b.id}`}
                    disabled={busyId === b.id + "confirm"}
                    onClick={() => act(b.id, "confirm")}
                    className="p-2 rounded-full hover:bg-[var(--mv-surface-2)] transition-colors"
                    aria-label="Bestätigen"
                    title="Bestätigen"
                  >
                    <Check className="w-4 h-4" strokeWidth={1.8} style={{ color: "var(--mv-secondary)" }} />
                  </button>
                )}
                {b.status === "confirmed" && (
                  <button
                    data-testid={`today-complete-${b.id}`}
                    disabled={busyId === b.id + "complete"}
                    onClick={() => act(b.id, "complete")}
                    className="p-2 rounded-full hover:bg-[var(--mv-surface-2)] transition-colors"
                    aria-label="Abschließen"
                    title="Abschließen"
                  >
                    <CheckCircle2 className="w-4 h-4" strokeWidth={1.8} style={{ color: "var(--mv-secondary)" }} />
                  </button>
                )}
                {b.status !== "cancelled" && b.status !== "completed" && (
                  <button
                    data-testid={`today-cancel-${b.id}`}
                    disabled={busyId === b.id + "cancel"}
                    onClick={() => { if (window.confirm("Termin stornieren?")) act(b.id, "cancel"); }}
                    className="p-2 rounded-full hover:bg-[var(--mv-surface-2)] transition-colors"
                    aria-label="Stornieren"
                    title="Stornieren"
                  >
                    <X className="w-4 h-4" strokeWidth={1.8} style={{ color: "#b91c1c" }} />
                  </button>
                )}
                <Link
                  to="/cms/bookings"
                  className="p-2 rounded-full hover:bg-[var(--mv-surface-2)] transition-colors"
                  aria-label="Details"
                  title="Details"
                >
                  <ChevronRight className="w-4 h-4" strokeWidth={1.8} style={{ color: "var(--mv-text-muted)" }} />
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
